import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { InfoOutlined } from '@mui/icons-material';
import Chromosomes from '../../../config/Chromosomes';
import APIUtils from '../../../utils/APIUtils';

const searchCriteriaOptions = [
  { value: 'hgvsName', label: 'Search by HGVS Name' },
  { value: 'chromLocation', label: 'Search by chromosome and GRCh38 location' },
  { value: 'advanced', label: 'Advanced Search' }
];

// Key used to preserve the search form inputs so they can be restored after the
// user navigates to variant details and clicks the browser back button.
const GENOTYPE_SEARCH_FORM_KEY = 'exploreGenotypeSearchForm';

const emptySettings = {
  hgvsString: '',
  chromosome: '',
  position: '',
  refAllele: '',
  altAllele: '',
  gene: '',
  condition: ''
};

// Maximum characters allowed in any free-text search field.
const MAX_SEARCH_INPUT_LENGTH = 100;

const isOverLimit = (value) => (value?.length || 0) > MAX_SEARCH_INPUT_LENGTH;
const lengthHelperText = (value) =>
  isOverLimit(value) ? `Maximum ${MAX_SEARCH_INPUT_LENGTH} characters allowed.` : undefined;

const getSavedForm = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GENOTYPE_SEARCH_FORM_KEY)) || {};
  } catch {
    return {};
  }
};

const SearchGenotypesSettingsPanel = ({
  classes,
  onSearchVariants,
  onClearResults,
  variantOptions,
  variantsToSearch,
  onVariantsToSearchChange,
  onSearchGenotypes
}) => {
  const savedForm = getSavedForm();
  const [searchCriteria, setSearchCriteria] = useState(savedForm.searchCriteria || '');
  const [tagOptions, setTagOptions] = useState([]);
  const [geneOptions, setGeneOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState(
    Array.isArray(savedForm.selectedTags) ? savedForm.selectedTags : []
  );
  const [matchAllTags, setMatchAllTags] = useState(!!savedForm.matchAllTags);
  const [settings, setSettings] = useState(
    savedForm.settings ? { ...emptySettings, ...savedForm.settings } : { ...emptySettings }
  );

  useEffect(() => {
    // Persist the form inputs whenever they change
    sessionStorage.setItem(
      GENOTYPE_SEARCH_FORM_KEY,
      JSON.stringify({ searchCriteria, settings, selectedTags, matchAllTags })
    );
  }, [searchCriteria, settings, selectedTags, matchAllTags]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await APIUtils.getTags();
        setTagOptions(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Failed to fetch tags', error);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const fetchGenes = async () => {
      try {
        const response = await APIUtils.getGenesLookup();
        setGeneOptions(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Failed to fetch genes', error);
      }
    };
    const fetchConditions = async () => {
      try {
        const response = await APIUtils.getConditionsLookup();
        setConditionOptions(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Failed to fetch conditions', error);
      }
    };
    fetchGenes();
    fetchConditions();
  }, []);

  const handleCriteriaChange = (newCriteria) => {
    setSearchCriteria(newCriteria);
    setSettings({ ...emptySettings });
    setSelectedTags([]);
    setMatchAllTags(false);
    onClearResults?.();
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Renders a clear (X) button overlaid on a select field, shown only when it
  // has a value. The wrapper ignores pointer events so the rest of the select
  // stays clickable; only the button itself receives clicks.
  const renderClearOverlay = (key, label) =>
    settings[key] ? (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: 32,
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          zIndex: 1
        }}
      >
        <IconButton
          size="small"
          aria-label={`clear ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            updateSetting(key, '');
          }}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ pointerEvents: 'auto' }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : null;

  // Build a request body containing only the params that have values
  const buildSearchBody = () => {
    if (searchCriteria === 'hgvsName') {
      const body = { searchType: 'hgvsName' };
      if (settings.hgvsString) body.hgvsGrch38Name = settings.hgvsString;
      return body;
    }

    if (searchCriteria === 'chromLocation') {
      const body = { searchType: 'chrAndLocation' };
      if (settings.chromosome) body.chr = settings.chromosome;
      if (settings.position) body.pos = settings.position;
      return body;
    }

    // advanced
    const body = { searchType: 'advanced' };
    if (settings.chromosome) body.chr = settings.chromosome;
    if (settings.position) body.pos = settings.position;
    if (settings.refAllele) body.ref = settings.refAllele;
    if (settings.altAllele) body.alt = settings.altAllele;
    if (settings.gene) body.gene = settings.gene;
    if (settings.condition) body.condition = settings.condition;
    if (selectedTags.length > 0) {
      body.tagIds = selectedTags.map((tag) => tag.tagId);
      body.tagMatchAll = matchAllTags;
    }
    return body;
  };

  const handleSearchVariants = () => {
    onSearchVariants?.(buildSearchBody());
  };

  // Advanced search requires at least one input to be filled
  const isAdvancedSearchEmpty =
    !settings.chromosome &&
    !settings.position &&
    !settings.refAllele &&
    !settings.altAllele &&
    !settings.gene &&
    !settings.condition &&
    selectedTags.length === 0;

  const hasLengthError =
    isOverLimit(settings.hgvsString) ||
    isOverLimit(settings.position) ||
    isOverLimit(settings.refAllele) ||
    isOverLimit(settings.altAllele);

  const renderChromosomeSelect = (id, label) => (
    <TextField
      size="small"
      id={id}
      className={classes?.muiInputs}
      value={settings.chromosome}
      onChange={(e) => updateSetting('chromosome', e.target.value)}
      variant="outlined"
      label={label}
      select
      fullWidth
      SelectProps={{
        MenuProps: { disableScrollLock: true, disablePortal: true }
      }}
    >
      {Chromosomes.map((chromosome) => (
        <MenuItem key={chromosome} value={chromosome}>
          {chromosome}
        </MenuItem>
      ))}
    </TextField>
  );

  // The "Variants to Search" multiselect and "Search Genotypes" button appear
  // below the "Search Variants" button row for every search criteria type. The
  // multiselect is disabled until a variant search returns results to choose
  // from, and Search Genotypes is disabled until at least one variant is added.
  // Rendered as direct items of the outer grid (sm 8 / sm 2) so the button aligns
  // with the Advanced Search "Search Variants" button and the row keeps the
  // default top padding instead of a doubled margin from a nested container.
  // Keep the field enabled whenever there are options to pick from OR variants
  // already added, so a prior search's selections stay editable even if a later
  // search returns no results.
  const variantsDisabled =
    (!variantOptions || variantOptions.length === 0) &&
    (!variantsToSearch || variantsToSearch.length === 0);
  const renderGenotypeSearchRow = () => (
    <>
      <Grid item xs={12} sm={10}>
        <FormControl fullWidth>
          <Autocomplete
            multiple
            size="small"
            className={classes?.muiInputs}
            id="variantsToSearch"
            options={variantOptions || []}
            value={variantsToSearch || []}
            onChange={(event, newValue) => onVariantsToSearchChange?.(newValue)}
            getOptionLabel={(option) => option?.variantName || ''}
            isOptionEqualToValue={(option, value) => option?.variantName === value?.variantName}
            renderOption={(props, option) => (
              // Key on variantName, the unique identifier for a variant. GRCh38
              // names can be duplicated across different variants, so keying on
              // them would cause duplicate-key warnings and duplicate additions.
              <li {...props} key={option.variantName}>
                {option?.variantName}
              </li>
            )}
            disabled={variantsDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Variants to Search"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {variantsDisabled && (
                        <Tooltip
                          title="Search variants first to populate this field"
                          placement="top"
                          arrow
                          componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}
                        >
                          <span
                            tabIndex={0}
                            role="img"
                            aria-label="Search variants first to populate this field"
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <InfoOutlined
                              style={{
                                fontSize: '1.1rem',
                                color: 'var(--info-color)',
                                filter: 'brightness(0.90)',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                                marginRight: '4px',
                              }}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={2}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => onSearchGenotypes?.()}
          disabled={!variantsToSearch || variantsToSearch.length === 0}
        >
          Search Genotypes
        </Button>
      </Grid>
    </>
  );

  return (
    <Grid container spacing={2} mb={2}>
      {/* Select Search Criteria */}
      <Box component={Grid} item xs={12} sm={5}>
        <FormControl fullWidth>
          <TextField
            size="small"
            id="selectSearchCriteria"
            className={classes?.muiInputs}
            value={searchCriteria}
            onChange={(e) => handleCriteriaChange(e.target.value)}
            variant="outlined"
            label="Select Search Criteria"
            select
            SelectProps={{
              MenuProps: { disableScrollLock: true, disablePortal: true }
            }}
          >
            {searchCriteriaOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      </Box>
      <Grid item xs={0} sm={7} />

      {/* Search by HGVS Name */}
      {searchCriteria === 'hgvsName' && (
        <>
          <Box component={Grid} item xs={12} sm={5}>
            <FormControl fullWidth>
              <TextField
                size="small"
                id="hgvsString"
                className={classes?.muiInputs}
                value={settings.hgvsString}
                onChange={(e) => updateSetting('hgvsString', e.target.value)}
                variant="outlined"
                label="Enter HGVS String"
                error={isOverLimit(settings.hgvsString)}
                helperText={lengthHelperText(settings.hgvsString)}
              />
            </FormControl>
          </Box>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearchVariants}
              disabled={!settings.hgvsString || isOverLimit(settings.hgvsString)}
            >
              Search Variants
            </Button>
          </Grid>
          <Grid item xs={0} sm={5} />
        </>
      )}

      {/* Search by chromosome and GRCh38 location */}
      {searchCriteria === 'chromLocation' && (
        <>
          <Box component={Grid} item xs={12} sm={3}>
            <FormControl fullWidth sx={{ position: 'relative' }}>
              {renderChromosomeSelect('selectChromosome', 'Select Chromosome')}
              {renderClearOverlay('chromosome', 'chromosome')}
            </FormControl>
          </Box>
          <Box component={Grid} item xs={12} sm={4}>
            <FormControl fullWidth>
              <TextField
                size="small"
                id="grch38Position"
                className={classes?.muiInputs}
                value={settings.position}
                onChange={(e) => updateSetting('position', e.target.value)}
                variant="outlined"
                label="Input GRCh38 Position"
                error={isOverLimit(settings.position)}
                helperText={lengthHelperText(settings.position)}
              />
            </FormControl>
          </Box>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearchVariants}
              disabled={!settings.chromosome || !settings.position || isOverLimit(settings.position)}
            >
              Search Variants
            </Button>
          </Grid>
          <Grid item xs={0} sm={3} />
        </>
      )}

      {/* Advanced Search */}
      {searchCriteria === 'advanced' && (
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="flex-end">
            {/* Left column */}
            <Grid item xs={12} sm={5}>
              <Grid container spacing={2}>
                <Box component={Grid} item xs={12}>
                  <FormControl fullWidth sx={{ position: 'relative' }}>
                    {renderChromosomeSelect('advancedChromosome', 'Chromosome')}
                    {renderClearOverlay('chromosome', 'chromosome')}
                  </FormControl>
                </Box>
                <Box component={Grid} item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      size="small"
                      id="refAllele"
                      className={classes?.muiInputs}
                      value={settings.refAllele}
                      onChange={(e) => updateSetting('refAllele', e.target.value)}
                      variant="outlined"
                      label="Ref Allele"
                      error={isOverLimit(settings.refAllele)}
                      helperText={lengthHelperText(settings.refAllele)}
                    />
                  </FormControl>
                </Box>
                <Box component={Grid} item xs={12}>
                  <FormControl fullWidth sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      id="gene"
                      className={classes?.muiInputs}
                      value={settings.gene}
                      onChange={(e) => updateSetting('gene', e.target.value)}
                      variant="outlined"
                      label="Gene"
                      select
                      SelectProps={{
                        MenuProps: { disableScrollLock: true, disablePortal: true }
                      }}
                    >
                      {geneOptions.length === 0 ? (
                        <MenuItem disabled>No options available</MenuItem>
                      ) : (
                        geneOptions.map((gene) => (
                          <MenuItem key={gene} value={gene}>
                            {gene}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                    {renderClearOverlay('gene', 'gene')}
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            {/* Middle column */}
            <Grid item xs={12} sm={5}>
              <Grid container spacing={2}>
                <Box component={Grid} item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      size="small"
                      id="advancedGrch38Position"
                      className={classes?.muiInputs}
                      value={settings.position}
                      onChange={(e) => updateSetting('position', e.target.value)}
                      variant="outlined"
                      label="GRCh38 Position"
                      error={isOverLimit(settings.position)}
                      helperText={lengthHelperText(settings.position)}
                    />
                  </FormControl>
                </Box>
                <Box component={Grid} item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      size="small"
                      id="altAllele"
                      className={classes?.muiInputs}
                      value={settings.altAllele}
                      onChange={(e) => updateSetting('altAllele', e.target.value)}
                      variant="outlined"
                      label="Alt Allele"
                      error={isOverLimit(settings.altAllele)}
                      helperText={lengthHelperText(settings.altAllele)}
                    />
                  </FormControl>
                </Box>
                <Box component={Grid} item xs={12}>
                  <FormControl fullWidth sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      id="condition"
                      className={classes?.muiInputs}
                      value={settings.condition}
                      onChange={(e) => updateSetting('condition', e.target.value)}
                      variant="outlined"
                      label="Condition"
                      select
                      SelectProps={{
                        MenuProps: { disableScrollLock: true, disablePortal: true }
                      }}
                    >
                      {conditionOptions.length === 0 ? (
                        <MenuItem disabled>No options available</MenuItem>
                      ) : (
                        conditionOptions.map((condition) => (
                          <MenuItem key={condition.acronym} value={condition.acronym}>
                            {condition.acronym ? `${condition.acronym} - ${condition.name}` : condition.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                    {renderClearOverlay('condition', 'condition')}
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            {/* Right column intentionally left for the relocated Search button in the Tags row */}
          </Grid>

          {/* Bottom row: Tags multiselect, Match all tags checkbox, and Search Variants button */}
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0 }}>
            <Grid item xs={12} sm={7}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  size="small"
                  className={classes?.muiInputs}
                  id="tags"
                  options={tagOptions}
                  value={selectedTags}
                  onChange={(event, newValue) => setSelectedTags(newValue)}
                  getOptionLabel={(option) => option?.tagName || ''}
                  isOptionEqualToValue={(option, value) => option?.tagId === value?.tagId}
                  renderInput={(params) => (
                    <TextField {...params} label="Tags" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="matchAllTags"
                    checked={matchAllTags}
                    onChange={(e) => setMatchAllTags(e.target.checked)}
                  />
                }
                label="Match all tags"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearchVariants}
                disabled={isAdvancedSearchEmpty || hasLengthError}
              >
                Search Variants
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Variants to Search + Search Genotypes row (present for every criteria) */}
      {searchCriteria && renderGenotypeSearchRow()}
    </Grid>
  );
};

export default SearchGenotypesSettingsPanel;
